import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from '@reach/router';
import { useStaticQuery, graphql } from 'gatsby';

export interface MetadataItem {
  name?: string;
  property?: string;
  content: string;
}
interface SEOProps {
  title?: string;
  description?: string;
  article?: boolean;
  noIndex?: boolean;
  additionalMetadata: MetadataItem[];
  disableTitleTemplate?: boolean;
}

interface SiteData {
  site: {
    siteMetadata: {
      title: string;
      description: string;
      siteUrl: string;
    };
  };
}

const SEO = ({
  title = '',
  description,
  article,
  noIndex = false,
  disableTitleTemplate,
  additionalMetadata = [],
}: SEOProps) => {
  const { pathname } = useLocation();
  const { site }: SiteData = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            siteUrl
          }
        }
      }
    `,
  );

  const metaDescription = description || site.siteMetadata.description;

  const botContent =
    'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';

  return (
    <Helmet
      htmlAttributes={{
        lang: 'en',
      }}
      title={title}
      titleTemplate={
        disableTitleTemplate ? undefined : `%s - ${site.siteMetadata.title}`
      }
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:url`,
          content: `${site.siteMetadata.siteUrl}${pathname}`,
        },
        {
          property: `og:type`,
          content: article ? `article` : `website`,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        {
          name: 'robots',
          content: noIndex ? 'none' : 'index, follow',
        },
        {
          name: 'googlebot',
          content: noIndex ? 'none' : botContent,
        },
        {
          name: 'bingbot',
          content: noIndex ? 'none' : botContent,
        },
        { property: 'og:locale', content: 'en_US' },
        { property: 'og:site_name', content: 'HOVER Inc' },
        { name: 'twitter:card', content: 'summary_large_image' },
        ...additionalMetadata,
      ]}
    />
  );
};

SEO.defaultProps = {
  title: '',
  description: '',
  article: true,
  noIndex: false,
};

export default SEO;
